import React, { useContext, useState, useReducer, useEffect } from 'react';
import { useDebounce } from 'usehooks-ts';
import {
  Typography,
  Stack,
  Grid,
  Autocomplete,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  InputSelect,
  InputButton,
  InputTextField
} from '@abbott-components/core';
import { useSnackbar } from 'notistack';
import {
  newWorkflowReducer,
  initialState,
  NewWorkflowActionKind,
  WorkflowServiceList,
  WorkflowTypes,
  WorkflowService
} from './newWorkflowReducer';
import { get, post } from '../Shared/ajax';
import LandingHeader from '../Landing/LandingHeader';
import SearchIcon from '@mui/icons-material/Search';
import BackToLandingButton from '../Shared/BackToLandingButton';
import { AppContext } from '../App/appReducer';
import { AppActionKind, AppPageKind } from '../App/types';

interface CreateNewWorkflowResponse {
  id: string;
}

interface CreateNewWorkflowRequest {
  name: string;
  workflowType: string;
  serviceId?: string;
}

const NewWorkflow: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const { dispatch: appDispatch } = useContext(AppContext);

  const [newWorkflowState, newWorkflowDispatch] = useReducer(
    newWorkflowReducer,
    initialState
  );
  const {
    forms,
    isLoading,
    workflowType,
    newWorkflowName,
    hasError,
    errorMessage,
    selectedForm,
    selectedId
  } = newWorkflowState;

  const [inputAutocomplete, setInputAutocomplete] = useState<string>('');
  const debouncedSearchTerm = useDebounce<string>(inputAutocomplete, 300);

  const fetchOHSFormsByName = async (searchTerm?: string) => {
    if (!searchTerm && workflowType === WorkflowTypes.OHS) {
      searchTerm = 'form'; // default search criteria for OHS workflow type
    } else if (!searchTerm || searchTerm.length < 3) {
      return;
    }

    try {
      newWorkflowDispatch({
        type: NewWorkflowActionKind.SET_ISLOADING,
        payload: true
      });

      const response = await get<WorkflowServiceList>(
        `DynamicWorkflowService?DynamicWorkflowServiceName=${searchTerm}&WorkflowType=OHS`
      );

      newWorkflowDispatch({
        type: NewWorkflowActionKind.SET_FORMS,
        payload: response.dynamicWorkflowServices
      });

      newWorkflowDispatch({
        type: NewWorkflowActionKind.SET_ISLOADING,
        payload: false
      });
    } catch (err) {
      enqueueSnackbar('An unexpected error occurred retrieving workflows', {
        variant: 'error'
      });

      newWorkflowDispatch({
        type: NewWorkflowActionKind.SET_ISLOADING,
        payload: false
      });

      return;
    }
  };

  const cleanInputData = () => {
    newWorkflowDispatch({
      type: NewWorkflowActionKind.SET_ERROR,
      payload: false
    });

    newWorkflowDispatch({
      type: NewWorkflowActionKind.SET_ERROR_MESSAGE,
      payload: ''
    });

    newWorkflowDispatch({
      type: NewWorkflowActionKind.SET_SELECTED_FORM,
      payload: null
    });

    newWorkflowDispatch({
      type: NewWorkflowActionKind.SET_SELECTED_FORM_ID,
      payload: ''
    });

    newWorkflowDispatch({
      type: NewWorkflowActionKind.SET_NEW_WORKFLOW_NAME,
      payload: ''
    });
  };

  const handleSelectWorkflowType = async (value: string) => {
    newWorkflowDispatch({
      type: NewWorkflowActionKind.SET_WORKFLOW_TYPE,
      payload: value
    });

    cleanInputData();

    await fetchOHSFormsByName();
  };

  const handleWorkflowName = async (character: string) => {
    newWorkflowDispatch({
      type: NewWorkflowActionKind.SET_NEW_WORKFLOW_NAME,
      payload: character
    });
  };

  const handleSelectedOptionAutocomplete = (
    event: React.SyntheticEvent<Element, Event>,
    value: string | null
  ) => {
    newWorkflowDispatch({
      type: NewWorkflowActionKind.SET_SELECTED_FORM,
      payload: value
    });

    forms.forEach((form) => {
      if (form.serviceName === value) {
        newWorkflowDispatch({
          type: NewWorkflowActionKind.SET_SELECTED_FORM_ID,
          payload: form.serviceId
        });
      }
    });
  };

  const handleInputChangeAutocomplete = (
    event: React.SyntheticEvent<Element, Event>,
    input: string
  ) => {
    setInputAutocomplete(input);
  };

  const secondaryInput = () => {
    switch (workflowType) {
      case WorkflowTypes.OHS:
        return (
          <Autocomplete
            id="Forms-autocomplete"
            value={selectedForm}
            onChange={handleSelectedOptionAutocomplete}
            inputValue={inputAutocomplete}
            onInputChange={handleInputChangeAutocomplete}
            loading={isLoading}
            options={forms.map((form: WorkflowService) => form.serviceName)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={hasError ? errorMessage : 'OHS Forms'}
                error={hasError}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon />,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        );

      case WorkflowTypes.OWT:
        return (
          <InputTextField
            name="textfieldOtherWorkflow"
            label="Add a Workflow Name"
            value={newWorkflowName}
            onTextChange={handleWorkflowName}
            errorText={hasError ? errorMessage : undefined}
          />
        );

      default:
        return (
          <InputTextField
            name="textfieldOtherWorkflow"
            label="Add a Workflow Name"
            value={newWorkflowName}
            onTextChange={handleWorkflowName}
            errorText={hasError ? 'Add a Workflow Name' : undefined}
          />
        );
    }
  };

  const handleContinueButton = () => {
    if (workflowType === WorkflowTypes.OHS) {
      if (selectedForm?.length === 0 || selectedForm === null) {
        newWorkflowDispatch({
          type: NewWorkflowActionKind.SET_ERROR,
          payload: true
        });
      } else {
        newWorkflowDispatch({
          type: NewWorkflowActionKind.SET_ERROR,
          payload: false
        });

        postNewWorkflow(selectedForm, WorkflowTypes.OHS);
      }
    }

    if (workflowType === WorkflowTypes.OWT) {
      if (newWorkflowName.length === 0) {
        newWorkflowDispatch({
          type: NewWorkflowActionKind.SET_ERROR,
          payload: true
        });
      } else {
        newWorkflowDispatch({
          type: NewWorkflowActionKind.SET_ERROR,
          payload: false
        });

        postNewWorkflow(newWorkflowName, WorkflowTypes.OWT);
      }
    }
  };

  const postNewWorkflow = async (workflowName: string, type: string) => {
    try {
      const response = await post<
        CreateNewWorkflowResponse,
        CreateNewWorkflowRequest
      >('DynamicWorkflow', {
        name: workflowName,
        workflowType: workflowType,
        serviceId: type === WorkflowTypes.OHS ? selectedId : ''
      });

      appDispatch({
        type: AppActionKind.UPDATE_WORKFLOWID,
        payload: response.id as string
      });

      appDispatch({
        type: AppActionKind.SET_APPPAGE,
        payload: AppPageKind.FORM_BUILDER
      });
    } catch (err) {
      const error = err as unknown as Error;

      newWorkflowDispatch({
        type: NewWorkflowActionKind.SET_ERROR,
        payload: true
      });

      newWorkflowDispatch({
        type: NewWorkflowActionKind.SET_ERROR_MESSAGE,
        payload: error.message
      });
    }
  };

  useEffect(() => {
    fetchOHSFormsByName();
  }, []);

  useEffect(() => {
    fetchOHSFormsByName(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  return (
    <Stack>
      <LandingHeader />
      <Stack sx={{ my: 0, textAlign: 'left', margin: '90px 30px 30px 30px' }}>
        <Grid container>
          <Grid item xs={6}>
            <BackToLandingButton />
          </Grid>
        </Grid>
      </Stack>

      <Stack sx={{ my: 2, textAlign: 'center' }}>
        <Typography variant="h1">Add New Workflow</Typography>
      </Stack>

      <Stack sx={{ alignItems: 'center' }}>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={4}
        >
          <Grid item xs={7}>
            <InputSelect
              name="selectWorkflowType"
              defaultValue={WorkflowTypes.OHS}
              label="Select a Workflow Type"
              onOptionSelectionChange={handleSelectWorkflowType}
            >
              <InputSelect.Option value={WorkflowTypes.OHS}>
                OHS Forms
              </InputSelect.Option>
              <InputSelect.Option value={WorkflowTypes.OWT}>
                Other Workflow Type
              </InputSelect.Option>
            </InputSelect>
          </Grid>

          <Grid item xs={7}>
            {secondaryInput()}
          </Grid>

          <Grid
            item
            xs={7}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <InputButton
              type="button"
              name="doTheThing"
              disabled={
                selectedForm === null && newWorkflowName.length === 0
                  ? true
                  : false
              }
              onClick={handleContinueButton}
            >
              Continue
            </InputButton>
          </Grid>
        </Grid>
      </Stack>
    </Stack>
  );
};

export default NewWorkflow;
